import {
  Box,
  Divider,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import type { DisplayMarketingCampaign } from "@common/interfaces/KYDMarketing";
import { buildMetaAdsManagerCampaignUrl } from "@common/utils/metaAdsManagerLinks";
import { ImportFromMetaControl } from "./ImportFromMeta";

export type MetaCampaignToolsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  campaign: DisplayMarketingCampaign;
  canImportFromMeta: boolean;
  isApplyingCampaignChanges: boolean;
  onImportApplyLoadingChange: (loading: boolean) => void;
  onAfterImport: () => void | Promise<void>;
};

export default function MetaCampaignToolsModal({
  isOpen,
  onClose,
  campaign,
  canImportFromMeta,
  isApplyingCampaignChanges,
  onImportApplyLoadingChange,
  onAfterImport,
}: MetaCampaignToolsModalProps) {
  const adsManagerUrl = useMemo(() => {
    if (!campaign.external_id || !campaign.account_id) return null;
    return buildMetaAdsManagerCampaignUrl({
      accountId: campaign.account_id,
      campaignExternalId: campaign.external_id,
    });
  }, [campaign.account_id, campaign.external_id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Meta campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.600" mb={1}>
                Meta campaign id
              </Text>
              <Text fontFamily="mono" fontSize="sm" wordBreak="break-all">
                {campaign.external_id ?? "—"}
              </Text>
            </Box>
            {adsManagerUrl ? (
              <ChakraLink
                href={adsManagerUrl}
                isExternal
                color="blue.600"
                fontWeight={500}
              >
                Open in Ads Manager
              </ChakraLink>
            ) : (
              <Text fontSize="sm" color="gray.600">
                Ads Manager link needs a synced Meta campaign and ad account on
                this campaign.
              </Text>
            )}
            {canImportFromMeta ? (
              <>
                <Divider borderColor="gray.200" />
                <ImportFromMetaControl
                  campaignId={campaign.id}
                  hasMetaExternalId={!!campaign.external_id}
                  canUse={canImportFromMeta}
                  isApplyingCampaignChanges={isApplyingCampaignChanges}
                  onImportApplyLoadingChange={onImportApplyLoadingChange}
                  onAfterImport={onAfterImport}
                />
              </>
            ) : null}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
